import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Minus, Plus, ShieldCheck, Package, Truck } from "lucide-react";
import { toast } from "sonner";
import { t, formatEur } from "@/i18n/sk";
import { getProduct, resolveImg } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { StockBadge } from "@/components/StockBadge";
import { ProductCard } from "@/components/ProductCard";
import { HoloBackground } from "@/components/HoloBackground";

export default function ProductDetail() {
  const { slug } = useParams();
  const { addItem, setOpen } = useCart();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [qty, setQty] = useState(1);
  const [activeImg, setActiveImg] = useState(0);

  useEffect(() => {
    setData(null); setNotFound(false); setQty(1); setActiveImg(0);
    getProduct(slug).then(setData).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return <div className="vault-bg mx-auto max-w-3xl px-4 py-32 text-center"><h1 className="font-display text-3xl font-bold text-white">Produkt nenájdený</h1><Link to="/serie" className="mt-6 inline-block text-cyan-300">← {t.nav.series}</Link></div>;
  if (!data) return <div className="vault-bg py-40 text-center text-slate-500">Načítavam…</div>;

  const p = data.product;
  const related = data.related || [];
  const soldOut = p.availability === "sold_out";
  const img = resolveImg(p.images?.[activeImg]);

  const handleAdd = () => {
    if (soldOut) return;
    addItem(p, qty);
    toast.success(t.actions.added, { description: `${qty}× ${p.name}` });
    setOpen(true);
  };

  return (
    <div className="vault-bg">
      <section className="relative">
        <HoloBackground />
        <div className="relative mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
          <Link to={`/serie/${p.expansion_slug}`} data-testid="back-to-expansion" className="mb-8 inline-flex items-center gap-1.5 text-sm text-slate-300 hover:text-white"><ArrowLeft className="h-4 w-4" /> {p.expansion_name}</Link>

          <div className="grid gap-10 lg:grid-cols-2">
            <div>
              <div className="holo-ring group relative flex aspect-square items-center justify-center overflow-hidden rounded-3xl border border-white/8 bg-vault-black/60 p-8">
                <span className="holo-sweep" />
                {img && <img src={img} alt={p.name} data-testid="product-main-image" className="h-full w-full object-contain" />}
                {p.placeholder_image && <span className="absolute bottom-4 right-4 rounded-full border border-white/15 bg-vault-black/70 px-2.5 py-1 text-[0.65rem] font-medium text-slate-300 backdrop-blur">{t.product.illustrative}</span>}
              </div>
              {p.images?.length > 1 && (
                <div className="mt-4 flex gap-3">
                  {p.images.map((im, i) => (
                    <button key={i} onClick={() => setActiveImg(i)} data-testid={`product-thumb-${i}`} className={`flex h-20 w-20 items-center justify-center overflow-hidden rounded-xl border bg-vault-black/60 p-2 transition-colors ${i === activeImg ? "border-cyan-glow/60" : "border-white/8 hover:border-white/20"}`}>
                      <img src={resolveImg(im)} alt="" className="h-full w-full object-contain" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div>
              <Link to={`/serie/${p.expansion_slug}`} className="overline mb-3 block hover:text-cyan-bright">{p.expansion_name}</Link>
              <h1 className="font-display text-3xl font-extrabold leading-tight tracking-tight text-white sm:text-4xl" data-testid="product-title">{p.name}</h1>

              <div className="mt-5 flex flex-wrap items-center gap-4">
                <span className="font-mono text-3xl font-black text-white" data-testid="product-price">{formatEur(p.price_eur)}</span>
                <StockBadge availability={p.availability} testId="product-stock-badge" />
              </div>

              <p className="mt-6 leading-relaxed text-slate-300">{p.description}</p>


              <div className="mt-8 flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-3 rounded-full border border-white/10 p-1.5">
                  <button onClick={() => setQty((q) => Math.max(1, q - 1))} data-testid="qty-decrease" disabled={soldOut} className="flex h-9 w-9 items-center justify-center rounded-full text-slate-300 hover:bg-white/10 hover:text-white disabled:opacity-40"><Minus className="h-4 w-4" /></button>
                  <span className="w-8 text-center font-semibold text-white" data-testid="qty-value">{qty}</span>
                  <button onClick={() => setQty((q) => q + 1)} data-testid="qty-increase" disabled={soldOut} className="flex h-9 w-9 items-center justify-center rounded-full text-slate-300 hover:bg-white/10 hover:text-white disabled:opacity-40"><Plus className="h-4 w-4" /></button>
                </div>
                <button onClick={handleAdd} disabled={soldOut} data-testid="product-add-to-cart" className="btn-glow inline-flex flex-1 items-center justify-center gap-2 rounded-full bg-gradient-to-r from-violet-glow to-cyan-glow px-8 py-3.5 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-40 disabled:shadow-none sm:flex-none">{soldOut ? t.availability.sold_out : t.actions.addToCart}</button>
              </div>

              <div className="mt-8 overflow-hidden rounded-xl border border-white/8">
                <dl className="divide-y divide-white/8 text-sm">
                  <div className="flex justify-between px-4 py-3"><dt className="text-slate-400">{t.product.type}</dt><dd className="font-medium text-white">{t.productTypes[p.product_type]}</dd></div>
                  <div className="flex justify-between px-4 py-3"><dt className="text-slate-400">{t.product.expansion}</dt><dd className="font-medium text-white">{p.expansion_name}</dd></div>
                  <div className="flex justify-between px-4 py-3"><dt className="text-slate-400">{t.product.availability}</dt><dd className="font-medium text-white">{t.availability[p.availability]}</dd></div>
                </dl>
              </div>

              <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-400">
                <span className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-cyan-300" /> Autentický produkt</span>
                <span className="flex items-center gap-2"><Package className="h-4 w-4 text-cyan-300" /> Chránené balenie</span>
                <span className="flex items-center gap-2"><Truck className="h-4 w-4 text-cyan-300" /> Odoslanie zo SK</span>
              </div>
            </div>
          </div>

          {related.length > 0 && (
            <div className="mt-20">
              <h2 className="mb-8 font-display text-2xl font-bold text-white">{t.product.related}</h2>
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
                {related.map((rp, i) => <ProductCard key={rp.id} product={rp} index={i} />)}
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
